import { IOrganisationTheme, IThemeColors } from 'fitbeat.models';
import { graphScaleText, scaleText } from './utils/scaling';

class Theme implements IThemeColors {
  public BODY = '#000000';

  public PRIMARY = '#000000';

  public PRIMARY_LIGHT = '#CACACA';

  public GREY_LIGHT = '#ECECEC';

  public GREY = '#B8B8B8';

  public GREY_FIGMA = '#F4F4F4';

  public GREY_FIGMA_MEDIUM = '#D8D8D8';

  public GREY_FIGMA_LIGHT = '#DDDDDD';

  public GREY_FIGMA_MEDIUM_LIGHT = '#979797';

  public GREY_MEDIUM_LIGHT = '#CACACA';

  public GREY_MEDIUM = '#7C7C7C';

  public GREY_DARK_MEDIUM = '#666666';

  public GREY_DARK_MEDIUM_DARK = '#454545';

  public GREY_DARK = '#343537';

  public CHARCOAL = '#1C1C1C';

  public DARK_CHARCOAL = '#333333';

  public GREEN = '#65C26E';

  public YELLOW = '#FFB02E';

  public RED = '#FF3D20';

  public RED_DARK = '#C60F13';

  public WHITE = '#FFFFFF';

  public BLACK = '#000000';

  public TEXT_DARK = '#181818';

  // TODO: remove these fonts and change references in the code
  public Default = 'Barlow-Medium';

  public CustomNormal = 'Barlow-Medium';

  public CustomBold = 'Barlow-SemiBold';

  // CustomNormalItalic is an exception:
  // all fonts need to match the PostScript name;
  // which is Italic and not RegularItalic for Regular
  // also make sure the file name matches this PostScript name
  public CustomNormalItalic = 'Barlow-MediumItalic';

  public Custom100 = 'Barlow-ExtraLight';

  public Custom200 = 'Barlow-Light';

  public Custom300 = 'Barlow-Medium';

  public Custom400 = 'Barlow-Medium';

  public Custom500 = 'Barlow-Medium';

  public Custom600 = 'Barlow-SemiBold';

  public Custom700 = 'Barlow-Bold';

  public Custom800 = 'Barlow-ExtraBold';

  public Custom900 = 'Barlow-Black';

  public Custom100Italic = 'Barlow-ThinItalic';

  public Custom500Italic = 'Barlow-MediumItalic';
  // Custom fonts above are used on legacy screens, use the Weight... properties instead

  public WeightNormalItalic = 'Barlow-MediumItalic';

  public Weight100 = 'Barlow-Thin';

  public Weight200 = 'Barlow-ExtraLight';

  public Weight300 = 'Barlow-Light';

  public Weight400 = 'Barlow-Regular';

  public Weight500 = 'Barlow-Medium';

  public Weight600 = 'Barlow-SemiBold';

  public Weight700 = 'Barlow-Bold';

  public Weight800 = 'Barlow-ExtraBold';

  public Weight900 = 'Barlow-Black';

  public Weight100Italic = 'Barlow-ThinItalic';

  public Weight500Italic = 'Barlow-MediumItalic';

  public organisationIconUrl: string | undefined = undefined;

  public organisationLogoUrl: string | undefined = undefined;

  public textStyle = {
    display1: {
      fontFamily: this.Weight400,
      fontSize: scaleText(28),
      color: this.TEXT_DARK,
    },
    display1Bold: {
      fontFamily: this.Weight700,
      fontSize: scaleText(28),
      color: this.TEXT_DARK,
    },
    headingLarge: {
      fontFamily: this.Weight700,
      fontSize: scaleText(22),
      color: this.TEXT_DARK,
    },
    heading: {
      fontFamily: this.Weight700,
      fontSize: scaleText(20),
      color: this.TEXT_DARK,
    },
    headingReg: {
      fontFamily: this.Weight400,
      fontSize: scaleText(20),
      color: this.TEXT_DARK,
    },
    heading2: {
      fontFamily: this.Weight700,
      fontSize: scaleText(18),
      color: this.TEXT_DARK,
    },
    bodyCopyReg: {
      fontFamily: this.Weight400,
      fontSize: scaleText(16),
      color: this.TEXT_DARK,
    },
    bodyCopyMed: {
      fontFamily: this.Weight500,
      fontSize: scaleText(16),
      color: this.TEXT_DARK,
    },
    bodyCopyBold: {
      fontFamily: this.Weight700,
      fontSize: scaleText(16),
      color: this.TEXT_DARK,
    },
    small: {
      fontFamily: this.Weight400,
      fontSize: scaleText(12),
      color: this.TEXT_DARK,
    },
    smallBold: {
      fontFamily: this.Weight700,
      fontSize: scaleText(12),
      color: this.TEXT_DARK,
    },
    mediumItalic: {
      fontFamily: this.Custom500Italic,
      fontSize: scaleText(16),
      color: this.TEXT_DARK,
    },
  };

  public graphStyle = {
    regular10: {
      fontFamily: this.Weight400,
      fontSize: graphScaleText(10),
      color: this.TEXT_DARK,
    },
    regular12: {
      fontFamily: this.Weight400,
      fontSize: graphScaleText(12),
      color: this.TEXT_DARK,
    },
    regular18: {
      fontFamily: this.Weight400,
      fontSize: scaleText(18),
      color: this.TEXT_DARK,
    },
    bold17: {
      fontFamily: this.Weight700,
      fontSize: scaleText(17),
      color: this.TEXT_DARK,
    },
    bold22: {
      fontFamily: this.Weight700,
      fontSize: scaleText(22),
      color: this.TEXT_DARK,
    },
    customLight13: {
      fontFamily: this.Custom200,
      fontSize: scaleText(13),
      color: this.TEXT_DARK,
    },
    customMedium14: {
      fontFamily: this.Custom500,
      fontSize: scaleText(14),
      color: this.TEXT_DARK,
    },
    customMedium16: {
      fontFamily: this.Custom500,
      fontSize: scaleText(16),
      color: this.TEXT_DARK,
    },
    customMedium18: {
      fontFamily: this.Custom300,
      fontSize: scaleText(18),
      color: this.TEXT_DARK,
    },
    customSemiBold16: {
      fontFamily: this.Custom600,
      fontSize: scaleText(16),
      color: this.TEXT_DARK,
    },
    customBold22: {
      fontFamily: this.Custom700,
      fontSize: scaleText(22),
      color: this.TEXT_DARK,
    },
  };
}

const theme = new Theme();

export function initializeTheme(customTheme?: IOrganisationTheme) {
  // TODO: nest colors underneath an static attribute. e.g Theme.color.ORANGE
  const defaultTheme = new Theme();
  Object.keys(defaultTheme).forEach((color) => {
    theme[color] = defaultTheme[color];
    if (customTheme?.colors[color]) {
      theme[color] = customTheme.colors[color];
    }
  });

  theme.organisationIconUrl = customTheme?.iconUrl;
  theme.organisationLogoUrl = customTheme?.logoUrl;
}

export function resetTheme() {
  initializeTheme();
}

export enum ThemeType {
  dark = 'dark',
  light = 'light',
}

export default theme;
